import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CartContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState({ items: [], item_count: 0, subtotal: 0 });
    const [loading, setLoading] = useState(false);

    const fetchCart = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/cart`, { withCredentials: true });
            setCart(response.data);
        } catch (error) {
            console.error('Error fetching cart:', error);
        }
    }, []);

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const addToCart = async (productId, quantity) => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${API}/cart/add`,
                { product_id: productId, quantity },
                { withCredentials: true }
            );
            setCart(response.data);
            return response.data;
        } catch (error) {
            console.error('Error adding to cart:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const updateQuantity = async (productId, quantity) => {
        return addToCart(productId, quantity);
    };

    const removeFromCart = async (productId) => {
        return addToCart(productId, 0);
    };

    const clearCart = async () => {
        try {
            const response = await axios.delete(`${API}/cart/clear`, { withCredentials: true });
            setCart(response.data);
        } catch (error) {
            console.error('Error clearing cart:', error);
        }
    };

    const getItemQuantity = (productId) => {
        const item = cart.items.find(i => i.product_id === productId);
        return item ? item.quantity : 0;
    };

    return (
        <CartContext.Provider value={{
            cart,
            loading,
            addToCart,
            updateQuantity,
            removeFromCart,
            clearCart,
            getItemQuantity,
            fetchCart
        }}>
            {children}
        </CartContext.Provider>
    );
};
